import { Star, StarBorderOutlined } from '@mui/icons-material';
import { Avatar } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { Slot } from '../../../../../interfaces/interfaces';
import { PresentationPreviewComponentInterface, UiComponentElementInterface } from '../../../../../interfaces/pagebuilder';
import { fetchAndSetFavoriteSlots } from '../../../../../reduxToolkit/slices/commonSlice';
import { RootState } from '../../../../../reduxToolkit/store';
import { useSelector, useDispatch } from '../../../../../reduxToolkit/typedRedux';
import { createDisplayLink, formatUTCDate } from '../../../../../services/helper';
import { followSlot, unfollowSlot } from '../../../../../services/services';
import EmptyComponent from '../../../pageBuilder/EmptyComponent';
import { TitleAndDescription } from '../../../pageBuilder/TitleAndDescription';

const PresentationPreviewList = ({ uiComponent }: UiComponentElementInterface<PresentationPreviewComponentInterface>) => {
  const { event } = useSelector(({ common }: RootState) => ({
    event: common.event,
    favoriteSlots: common.favoriteSlots,
  }));

  const dispatch = useDispatch();

  const user = useSelector((state: RootState) => state.common.user);
  const slots = useSelector((state: RootState) => state.common.slots);
  const favoriteSlots = useSelector((state: RootState) => state.common.favoriteSlots);

  const { t } = useTranslation();

  async function _followSlot(talkid: string) {
    if (!event) return;
    await followSlot(talkid, event?._id);
    dispatch(fetchAndSetFavoriteSlots(event?._id));
  }

  async function _unfollowSlot(talkid: string) {
    if (!event) return;
    await unfollowSlot(talkid, event?._id);
    dispatch(fetchAndSetFavoriteSlots(event?._id));
  }

  const isSlotFavorited = (slot: Slot) => !!favoriteSlots?.find((favorite) => favorite.slot._id === slot._id);

  return (
    <>
      <TitleAndDescription component={uiComponent!} />
      <div
        className="grid gap-2 w-full"
        style={{
          color: uiComponent?.styling?.textColor || '#000',
        }}>
        {slots && slots.length > 0 ? (
          <>
            {uiComponent?.title && <div className="mt-12 text-4xl">{uiComponent?.title}</div>}
            {slots.map((talk: any) => {
              return (
                <div className="flex gap-4 @sm:m-10" key={talk._id}>
                  {user && (
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSlotFavorited(talk)) {
                          _followSlot(talk._id);
                        } else {
                          _unfollowSlot(talk._id);
                        }
                      }}>
                      {!isSlotFavorited(talk) ? <StarBorderOutlined style={{ fill: '#FFCA28' }} /> : <Star style={{ fill: '#FFCA28' }} />}
                    </div>
                  )}
                  <div className="flex items-center">
                    <Avatar className="w-14 h-14 @sm:h-20 @sm:w-20" src={createDisplayLink(talk.meetingroom.image)} alt="slot image" />
                  </div>
                  <div className="flex gap-2 justify-between w-full">
                    <div className="w-full @sm:w-auto space-y-2 @sm:space-y-1">
                      <div className="opacity-50 text-md @sm:text-base [overflow-wrap:anywhere]">{talk.meetingroom.booth?.name}</div>
                      <div className="max-w-full text-md @sm:text-lg [overflow-wrap:anywhere]">{talk?.meetingroom?.name}</div>
                      <div className="max-w-full text-md @sm:text-lg font-light [overflow-wrap:anywhere]" style={{ overflowWrap: 'anywhere' }}>
                        {talk.meetingroom.contactperson}
                      </div>
                    </div>

                    {uiComponent?.showTime && (
                      <div className="flex flex-col justify-center @sm:shrink-0">
                        <span className="[overflow-wrap:anywhere] max-w-full">{talk.stage?.name}</span>
                        <span>{talk?.datetime_start && `${formatUTCDate(new Date(talk?.datetime_start), 'HH:mm')} Uhr`}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <EmptyComponent componentName={t('pagebuilder:presentation-preview')} />
        )}
      </div>
    </>
  );
};

export default PresentationPreviewList;
